/**
 * IntegrationsSettings - Card-based integrations page
 *
 * Displays available integrations as cards:
 * - LinkedIn: Import profile data
 * - Slack: Post notifications
 * - Calendar: Sync events
 *
 * Design Philosophy:
 * - Visual status (connected/not connected)
 * - Simple connect/disconnect actions
 * - Brief description of what each does
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Linkedin,
  MessageSquare,
  Calendar,
  CheckCircle2,
  XCircle,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { currentCommunityAtom } from '@/lib/stores/community';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/atoms/button';
import { cn } from '@/lib/utils';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBgClass: string;
  isConnected: boolean;
  connectedAccount?: string;
  setupUrl: string;
}

export function IntegrationsSettings() {
  const community = useAtomValue(currentCommunityAtom);

  // Fetch integration statuses
  const { data: integrationStatus, isLoading } = useQuery({
    queryKey: ['integrations', community?.id],
    queryFn: async () => {
      if (!community?.id) return {};

      // Would fetch from integrations table
      // For now, return mock data
      return {
        linkedin: { connected: false },
        slack: { connected: false },
        calendar: { connected: false },
      };
    },
    enabled: !!community?.id,
  });

  const integrations: Integration[] = [
    {
      id: 'linkedin',
      name: 'LinkedIn',
      description:
        'Import member profiles and keep them up to date automatically.',
      icon: Linkedin,
      iconBgClass: 'bg-[#0A66C2]/10',
      isConnected: integrationStatus?.linkedin?.connected || false,
      connectedAccount: integrationStatus?.linkedin?.account,
      setupUrl: '/legacy-settings/integrations/linkedin',
    },
    {
      id: 'slack',
      name: 'Slack',
      description:
        'Post community updates and receive notifications in your workspace.',
      icon: MessageSquare,
      iconBgClass: 'bg-[#4A154B]/10',
      isConnected: integrationStatus?.slack?.connected || false,
      connectedAccount: integrationStatus?.slack?.workspace,
      setupUrl: '/legacy-settings/integrations/slack',
    },
    {
      id: 'calendar',
      name: 'Google Calendar',
      description: 'Sync community events with your calendar.',
      icon: Calendar,
      iconBgClass: 'bg-[#4285F4]/10',
      isConnected: integrationStatus?.calendar?.connected || false,
      connectedAccount: integrationStatus?.calendar?.email,
      setupUrl: '/legacy-settings/integrations/calendar',
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <header className="mb-8">
        <Link
          to="/settings/settings-v2"
          className="inline-flex items-center text-[14px] text-[#6B6966] hover:text-[#1A1A19] mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Settings
        </Link>
        <h1 className="serif-display text-[28px] text-[#1A1A19]">
          Integrations
        </h1>
        <p className="text-[15px] text-[#6B6966] mt-1">
          Connect external services to enhance your community
        </p>
      </header>

      {/* Integration Cards */}
      <div className="space-y-4">
        {integrations.map((integration) => {
          const Icon = integration.icon;
          return (
            <div
              key={integration.id}
              className={cn(
                'p-6 bg-white border rounded-xl',
                integration.isConnected
                  ? 'border-green-200'
                  : 'border-[#E8E6E3]'
              )}
            >
              <div className="flex items-start">
                <div
                  className={cn(
                    'w-12 h-12 rounded-lg flex items-center justify-center',
                    integration.iconBgClass
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>

                <div className="flex-1 ml-4">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-[17px] font-medium text-[#1A1A19]">
                      {integration.name}
                    </h3>
                    {integration.isConnected ? (
                      <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-[11px] font-medium rounded-full">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 bg-[#F5F3F0] text-[#6B6966] text-[11px] font-medium rounded-full">
                        Not connected
                      </span>
                    )}
                  </div>

                  <p className="text-[14px] text-[#6B6966] mt-1">
                    {integration.description}
                  </p>

                  {integration.isConnected && integration.connectedAccount && (
                    <p className="text-[13px] text-[#6B6966] mt-2">
                      Connected as:{' '}
                      <span className="font-medium">
                        {integration.connectedAccount}
                      </span>
                    </p>
                  )}

                  <div className="mt-4">
                    <Link to={integration.setupUrl}>
                      <Button
                        variant={
                          integration.isConnected ? 'outline' : 'default'
                        }
                        size="sm"
                        className={cn(
                          integration.isConnected
                            ? 'border-[#E8E6E3]'
                            : 'bg-[#CC785C] hover:bg-[#CC785C]/90 text-white'
                        )}
                      >
                        {integration.isConnected ? 'Manage' : 'Connect'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* More Integrations Link */}
      <div className="mt-8 pt-6 border-t border-[#E8E6E3]">
        <Link
          to="/legacy-settings/integrations"
          className="inline-flex items-center text-[14px] text-[#CC785C] hover:underline"
        >
          View all integrations
          <ExternalLink className="w-3.5 h-3.5 ml-1" />
        </Link>
      </div>
    </div>
  );
}
